/* eslint-disable default-case */
import React, {Fragment, useState, useEffect, useContext, useReducer, useRef, useCallback} from 'react';
import {Row, Col, TreeSelect, notification, Button, Popconfirm, Tooltip} from 'antd';
import {ApiView} from './api-view';
import {AppContext} from '../state';
import {useRouteMatch, useHistory, useLocation} from 'react-router-dom';
import {ImportSpecDialog} from '../components/import-spec-dialog';
import {OpenApiContext} from '../openapi-context';
import {VersionPanel} from '../components/version-panel';
import md5 from 'js-md5';
import {set, cloneDeep, unset, get} from 'lodash';
import qs from 'qs';
import openAPISchemaValidator from '../utils/openapi-schema-validator';
import {RollbackOutlined, SyncOutlined, TagOutlined} from '@ant-design/icons';
import {PublishVersionDialog} from '../components/publish-version-dialog';

function makeSpecKey(params) {
    const hasher = md5.create();
    hasher.update(params.env).update(params.category).update(params.key);
    if (params.version) {
        hasher.update(params.version);
    }
    return hasher.hex();
}

// §9.4 onSpecChange contract: when the spec mutates, fire a structured
// event so portal can refresh its Comments / audit views. We map each
// spec-reducer action type onto an absolute json_pointer + change_type.
// Unknown actions fall through to root + 'update' so portal still gets
// the signal — finer-grained pointers come from the action shape itself,
// not a generic walk.
//
// 'load' is excluded — it's the initial fetch from disk, not a user
// mutation, and firing on it would generate spurious comment refreshes
// every time the spec is reloaded after navigation.
const SPEC_ACTION_TO_EVENT = {
    'rename-model': (a) => ({json_pointer: `/components/schemas/${a.name}`, change_type: 'update'}),
    'update-model': (a) => ({json_pointer: `/components/schemas/${a.model && a.model.name}`, change_type: 'update'}),
    'add-model': (a) => ({json_pointer: `/components/schemas/${a.name}`, change_type: 'add'}),
    'delete-model': (a) => ({json_pointer: `/components/schemas/${a.name}`, change_type: 'delete'}),
    'update-interface': (a) => {
        const container = a.intf && a.intf.source === 'webhooks' ? 'webhooks' : 'paths';
        const seg = a.intf && (a.intf.path || a.intf.name);
        return {json_pointer: `/${container}/${(seg || '').replace(/~/g, '~0').replace(/\//g, '~1')}`, change_type: 'update'};
    },
    'add-interface': (a) => ({json_pointer: `/paths/${(a.path || '').replace(/~/g, '~0').replace(/\//g, '~1')}`, change_type: 'add'}),
    'delete-interface': (a) => ({json_pointer: `/paths/${(a.path || '').replace(/~/g, '~0').replace(/\//g, '~1')}`, change_type: 'delete'}),
    'add-webhook': (a) => ({json_pointer: `/webhooks/${a.name}`, change_type: 'add'}),
    'delete-webhook': (a) => ({json_pointer: `/webhooks/${a.name}`, change_type: 'delete'}),
    'update-info': () => ({json_pointer: '/info', change_type: 'update'}),
    'update-servers': () => ({json_pointer: '/servers', change_type: 'update'}),
    'update-contact': () => ({json_pointer: '/info/contact', change_type: 'update'}),
    'update-security': () => ({json_pointer: '/security', change_type: 'update'}),
    'update-parameter-def': (a) => ({json_pointer: `/components/parameters/${a.parameter && a.parameter.spec && a.parameter.spec.name}`, change_type: 'update'}),
    'delete-parameter-def': (a) => ({json_pointer: `/components/parameters/${a.name}`, change_type: 'delete'}),
    'update-security-scheme-def': (a) => ({json_pointer: `/components/securitySchemes/${a.securityScheme && a.securityScheme.name}`, change_type: 'update'}),
    'delete-security-scheme-def': (a) => ({json_pointer: `/components/securitySchemes/${a.name}`, change_type: 'delete'}),
    'update-global-security': () => ({json_pointer: '/security', change_type: 'update'}),
};

export function Library({mobile}) {
    const openapiContext = useContext(OpenApiContext);
    const {backend, dispatch, editorProps = {}} = useContext(AppContext);
    // Null-guard for portal-mounted mode: when <TenetEditor> renders at
    // a portal route that doesn't match the editor's URL pattern (e.g.
    // portal's '/' or '/admin'), useRouteMatch returns null and every
    // subsequent `match.params.*` destructure crashes the React tree
    // (blank screen, can't reach the user menu to log out). Default the
    // params to an empty object so reads return undefined and the
    // useEffects bail out gracefully. (Gap #4 from portal Day 11.)
    const match = useRouteMatch('/:env/standards/:category/:key/:version?/:view?') || {params: {}};
    const {search} = useLocation();
    const history = useHistory();
    const [library, setLibrary] = useState([]);
    const [selection, setSelection] = useState();
    const [versions, setVersions] = useState();
    const [version, setVersion] = useState();
    const [dirty] = useState(false);
    const [specKey, setSpecKey] = useState();
    const [currentHash, setCurrentHash] = useState();
    const [origHash, setOrigHash] = useState();
    const [isLoading, setIsLoading] = useState(false);

    const [spec, updateSpec] = useReducer(function (draft, action) {
        switch (action.type) {
            case 'load':
                return action.spec;
            case 'rename-model': {
                const target = get(draft.components.schemas, action.previous);
                if (target) {
                    unset(draft.components.schemas, action.previous);
                    draft.components.schemas[action.name] = target;
                }
                break;
            }
            case 'update-model':
                draft.components.schemas[action.model.name] = action.model.spec;
                break;
            case 'delete-model':
                unset(draft.components.schemas, action.name);
                break;
            case 'update-servers':
                set(draft, action.servers.name, action.servers.value);
                break;
            case 'update-contact':
                set(draft, action.contact.name, action.contact.value);
                break;
            case 'update-security':
                set(draft, action.security.name, action.security.value);
                break;
            case 'add-model':
                draft.components = draft.components || {};
                if (action.schemaType === 'array') {
                    action.model = {type: 'array', items: {type: 'object', properties: {}}};
                } else if (action.schemaType === 'combined') {
                    action.model = {};
                    action.model[action.operator] = [];
                } else if (action.schemaType === 'object') {
                    action.model = {type: 'object', properties: {}};
                } else {
                    action.model = {type: action.schemaType};
                }

                switch (action.modelType) {
                    case 'schema':
                        draft.components.schemas = draft.components.schemas || {};
                        draft.components.schemas[action.name] = action.model;
                        break;
                    case 'response':
                        action.model.description = '';
                        action.model.content = {};
                        action.model.headers = {};
                        draft.components.responses = draft.components.responses || {};
                        draft.components.responses[action.name] = action.model;
                        break;
                    case 'parameter':
                        action.model.name = action.name;
                        action.model.in = 'query';
                        action.description = '';
                        action.model.schema = {
                            type: action.model.type || 'object',
                        };
                        unset(action.model, 'type');
                        draft.components.parameters = draft.components.parameters || {};
                        draft.components.parameters[action.name] = action.model;
                        break;
                    case 'header':
                        action.model.description = '';
                        action.model.schema = {type: action.schemaType};
                        draft.components.headers = draft.components.headers || {};
                        draft.components.headers[action.name] = action.model;
                        break;
                    case 'example':
                        draft.components.examples = draft.components.examples || {};
                        draft.components.examples[action.name] = action.model;
                        break;
                    case 'requestBody':
                        action.model.description = '';
                        action.model.content = {};
                        draft.components.requestBodies = draft.components.requestBodies || {};
                        draft.components.requestBodies[action.name] = action.model;
                        break;
                    case 'securityScheme':
                        draft.components.securitySchemes = draft.components.securitySchemes || {};
                        draft.components.securitySchemes[action.name] = {
                            type: 'http',
                            description: action.model.description || '',
                            name: action.model.name || action.name,
                            in: 'header',
                            scheme: 'bearer',
                            bearerFormat: 'JWT',
                        };
                        break;
                }
                break;
            case 'update-interface': {
                // source === 'webhooks' writes under spec.webhooks (3.1 only).
                // source === 'paths' (default) writes under spec.paths.
                const container = action.intf && action.intf.source === 'webhooks' ? 'webhooks' : 'paths';
                draft[container] = draft[container] || {};
                draft[container][action.intf.path] = action.intf.spec;
                break;
            }
            case 'delete-interface':
                unset(draft.paths, action.path);
                break;
            case 'add-interface':
                draft.paths = draft.paths || {};
                draft.paths[action.path] = action.intf;
                break;
            case 'add-webhook':
                draft.webhooks = draft.webhooks || {};
                draft.webhooks[action.name] = action.webhook;
                break;
            case 'delete-webhook':
                unset(draft.webhooks, action.name);
                break;
            case 'update-info':
                set(draft, 'info', action.spec);
                break;
            case 'update-parameter-def': {
                if (action.parameter.name !== action.parameter.spec.name) {
                    unset(draft.components.parameters, action.parameter.name);
                }
                draft.components.parameters[action.parameter.spec.name] = action.parameter.spec;
                break;
            }
            case 'delete-parameter-def': {
                unset(draft.components, `parameters.${action.name}`);
                break;
            }
            case 'update-security-scheme-def': {
                draft.components.securitySchemes[action.securityScheme.name] = action.securityScheme.spec;
                break;
            }
            case 'delete-security-scheme-def': {
                unset(draft.components, `securitySchemes.${action.name}`);
                break;
            }
            case 'update-global-security': {
                set(draft, 'security', action.globalSecurity);
                break;
            }
            default:
                notification.error({title: `Unknown action ${action.type}. Please report this to developers!`, placement: 'bottomRight'});
                throw new Error('unknown action:' + action.type);
        }
        return cloneDeep(draft);
    });

    // §9.4 onSpecChange: stash the most recent dispatched action so the
    // auto-save useEffect (which fires on the post-mutation spec) knows
    // what changed and can emit a structured event to the portal.
    // Wrapping updateSpec is cleaner than touching every call site
    // (~30 dispatches across the code base) — the wrapper preserves the
    // dispatch contract and just captures one ref before calling through.
    const lastSpecActionRef = useRef(null);
    const wrappedUpdateSpec = useCallback((action) => {
        lastSpecActionRef.current = action;
        updateSpec(action);
    }, []);

    async function loadLibrary() {
        const lib = await backend.loadLibrary();
        // Workgroup (parent) nodes are just grouping labels; their `value`
        // is the workgroup name alone (no `-spec` suffix), so selecting
        // one produces an unroutable URL. Mark them non-selectable so the
        // TreeSelect only accepts leaf clicks.
        const withSelectability = (lib || []).map((wg) => ({
            ...wg,
            selectable: false,
        }));
        setLibrary(withSelectability);
    }

    useEffect(() => {
        loadLibrary();
    }, [match.params.env, match.params.category, match.params.key, match.params.version]);

    useEffect(() => {
        if (match.params.category && match.params.key) {
            setSelection(`${match.params.category}-${match.params.key}`);
        }
    }, [library]);

    useEffect(() => {
        setVersion(match.params.version || 'latest');
    }, [match.params.version, versions]);

    useEffect(() => {
        async function loadVersions() {
            const versions = await backend.loadVersions(match.params.category, match.params.key);
            setVersions(versions);
        }
        loadVersions();
    }, [library]);

    useEffect(() => {
        async function commitChanges() {
            const result = await backend.commitChanges({
                category: match.params.category,
                key: match.params.key,
                spec,
                message: openapiContext.state.message,
            });
            if (result) {
                notification.success({title: `Changes successfully saved!`, placement: 'bottomRight'});
            }
        }

        if (spec) {
            const hasher = md5.create();
            hasher.update(JSON.stringify(spec)).update(match.params.category).update(match.params.key);
            if (match.params.version) {
                hasher.update(match.params.version);
            }
            const newHash = hasher.hex();
            setCurrentHash(newHash);
            if (newHash !== origHash) {
                // only for updates... not when loading spec
                if (makeSpecKey(match.params) === specKey) {
                    commitChanges();
                }
                openapiContext.dispatch({type: 'setSpec', spec});

                // §9.4 onSpecChange: emit a structured event for the
                // portal's Comments / audit machinery. The 'load' case
                // is filtered out — that's the initial fetch, not a
                // user mutation — and unknown action types fall back
                // to a coarse root-level 'update' so portal still gets
                // a poke on every change.
                const cb = editorProps.onSpecChange;
                const action = lastSpecActionRef.current;
                if (typeof cb === 'function' && action && action.type !== 'load') {
                    const mapper = SPEC_ACTION_TO_EVENT[action.type];
                    const detail = mapper
                        ? mapper(action)
                        : {json_pointer: '/', change_type: 'update'};
                    cb({
                        spec_path: match.params.category && match.params.key
                            ? `${match.params.category}/${match.params.key}`
                            : null,
                        json_pointer: detail.json_pointer,
                        change_type: detail.change_type,
                        // 'human' until Phase C — that's when AI-applied
                        // edits route through their own dispatch path
                        // and stamp the action with source/conversation.
                        source: 'human',
                        conversation_id: undefined,
                    });
                }
            }
        }
    }, [spec, match.params.category, match.params.key, match.params.version]);

    useEffect(() => {
        const subscription = backend.event$.subscribe(async () => {
            loadLibrary();
            loadSpec();
        });

        return function () {
            subscription.unsubscribe();
        };
    }, [match.params.category, match.params.key]);

    async function loadSpec() {
        if (match.params.category && match.params.key) {
            try {
                setIsLoading(true);
                let spec = await backend.loadSpec({
                    category: match.params.category,
                    key: match.params.key,
                    version: match.params.version || 'latest',
                });
                const hasher = md5.create();
                hasher.update(JSON.stringify(spec)).update(match.params.category).update(match.params.key).update(match.params.version || 'latest');
                const newHash = hasher.hex();
                setCurrentHash(newHash);
                setOrigHash(newHash);

                // Validate spec
                spec = openAPISchemaValidator(spec, openapiContext);

                updateSpec({type: 'load', spec});
                openapiContext.dispatch({type: 'setSpec', spec});
                if (search) {
                    openapiContext.dispatch({type: 'change-role', role: qs.parse(search.substring(1)).role});
                }
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        }
    }

    useEffect(() => {
        setSpecKey(makeSpecKey(match.params));
        loadSpec();
    }, [match.params.env, match.params.category, match.params.key, match.params.version]);

    useEffect(() => {
        if (spec) {
            openAPISchemaValidator(spec, openapiContext);
        }
    }, [spec]);

    function jumpToSpec(val) {
        if (!val) return;
        // Leaf values are encoded as `<workgroup>-<specKey>` by the backend
        // (services/library.js). Spec keys themselves can contain hyphens
        // (e.g. "fullopenapi-3"), so split on the FIRST hyphen only — a
        // plain `.split('-')` loses every segment after the second one and
        // produces a 404/500 on the next load.
        //
        // Parent (workgroup-only) values have no hyphen at all. Selecting
        // one used to push "/<env>/standards/<workgroup>/undefined/..." and
        // 500 on the backend. Bail early instead.
        const sep = val.indexOf('-');
        if (sep < 0) return;
        const workgroup = val.slice(0, sep);
        const specKey = val.slice(sep + 1);
        dispatch({type: 'clear-breadcrumb'});
        history.push(`/${match.params.env}/standards/${workgroup}/${specKey}/latest/overview`);
    }

    async function onSave(commitDetails) {
        await backend.commitChanges(match.params.category, match.params.key, spec, commitDetails);
        if (version !== 'latest') {
            const pathname = history.location.pathname.replace(`/${version}/`, `/${commitDetails.version}/`);
            setVersion(commitDetails.version);
            history.push(pathname);
        } else {
            await loadSpec();
        }
    }

    function onRollback() {
        loadSpec();
    }

    function handleChangeVersion(newVersion) {
        setVersion(newVersion);
        const pathname = history.location.pathname.replace(`/${version}/`, `/${newVersion}/`);
        setOrigHash(null);
        history.push(pathname);
    }

    const canEdit = () => openapiContext.SUPPORTED_EDITOR_ROLES.indexOf(openapiContext.state.role) !== -1 && !mobile;

    async function handleResetSandbox() {
        try {
            await backend.rollback({workgroup: match.params.category, key: match.params.key});
            notification.success({title: `Your pending changes were rollback. Spec is reset to latest release`, placement: 'bottomRight'});
        } catch (err) {
            notification.error({title: `Unable to reset sandbox. Please contact support`, placement: 'bottomRight'});
        }
    }

    async function handleSyncChanges() {
        try {
            await backend.syncChanges({workgroup: match.params.category, key: match.params.key});
            notification.success({title: `Latest upstream changes were synced in your private workspace`, placement: 'bottomRight'});
        } catch (err) {
            notification.error({title: `Unable to sync changes. You might need to click reset`, placement: 'bottomRight'});
        }
    }

    return (
        <Fragment>
            <Row>
                <Col
                    xs={mobile ? 24 : 6}
                    style={{
                        padding: '0.5em',
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '-5px',
                    }}>
                    <TreeSelect
                        showSearch
                        disabled={openapiContext.state.editMode}
                        style={{width: canEdit() ? '90%' : '100%'}}
                        styles={{popup: {root: {maxHeight: 400, overflow: 'auto'}}}}
                        placeholder="Library Navigator"
                        allowClear
                        value={selection}
                        treeDefaultExpandAll
                        treeData={library}
                        onChange={jumpToSpec}
                    />
                    {canEdit() && <ImportSpecDialog disabled={openapiContext.state.editMode} backend={backend} params={match.params} spec={spec} />}
                </Col>
                <Col lg={18} xs={23}>
                    <Row style={{marginTop: 5}}>
                        <Col span={10}>
                            <VersionPanel versions={versions} version={version} changeVersion={handleChangeVersion} dirty={dirty} onCommit={onSave} onRollback={onRollback} mobile={mobile} />
                        </Col>
                        <Col span={14}>
                            {canEdit() && (
                                <>
                                    <PublishVersionDialog workgroup={match.params.category} specKey={match.params.key} spec={spec} />
                                    <Tooltip placement="bottom" title="Pull changes from other users into your private workspace">
                                        <Button style={{marginTop: 7, marginLeft: 3}} onClick={handleSyncChanges}>
                                            <SyncOutlined />
                                        </Button>
                                    </Tooltip>

                                    <Popconfirm
                                        okText="Yes"
                                        cancelText="No"
                                        placement="bottom"
                                        title="Reseting your sandbox will delete all your pending changes. Do you want to continue?"
                                        onConfirm={handleResetSandbox}>
                                        <Button style={{marginTop: 7, marginLeft: 3}} type="primary" danger title="Reset">
                                            <RollbackOutlined />
                                        </Button>
                                    </Popconfirm>
                                </>
                            )}
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <ApiView mobile={mobile} currentHash={currentHash} version={version} dirty={dirty} isLoading={isLoading} spec={spec} updateSpec={wrappedUpdateSpec} />
                </Col>
            </Row>
        </Fragment>
    );
}
